import React, { useState, useEffect, useRef, useCallback } from 'react';
import { colors, FONT, radii } from '../../theme';
import { X, Undo2, Redo2, Send, Pen, Type, Eraser } from 'lucide-react';

const MAX_HISTORY = 30;
const COLORS = ['#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#007AFF', '#5856D6', '#AF52DE', '#FFFFFF', '#000000'];

export default function ImageEditor({ imageUrl, onSave, onCancel }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#FF3B30');
  const [brushSize, setBrushSize] = useState(4);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Text tool state
  const [textInput, setTextInput] = useState(null); // { x, y } position or null
  const [textValue, setTextValue] = useState('');

  // History for undo/redo
  const historyRef = useRef([]);
  const historyIdxRef = useRef(-1);
  const [, forceUpdate] = useState(0);

  // Drawing state
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const pointsRef = useRef([]);

  // ─── Setup canvas when image loads ───────────────────────
  const setupCanvas = useCallback(() => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!img || !canvas || !container) return;

    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const imgRatio = img.naturalWidth / img.naturalHeight;
    const containerRatio = cw / ch;

    let w, h;
    if (imgRatio > containerRatio) {
      w = cw;
      h = cw / imgRatio;
    } else {
      h = ch;
      w = ch * imgRatio;
    }

    canvas.width = w;
    canvas.height = h;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    img.style.width = w + 'px';
    img.style.height = h + 'px';

    // Save initial blank state
    const ctx = canvas.getContext('2d');
    pushHistory(ctx.getImageData(0, 0, canvas.width, canvas.height));
  }, []);

  useEffect(() => {
    if (imgLoaded) setupCanvas();
  }, [imgLoaded, setupCanvas]);

  useEffect(() => {
    const onResize = () => { if (imgLoaded) setupCanvas(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [imgLoaded, setupCanvas]);

  // ─── History management ──────────────────────────────────
  const pushHistory = (imageData) => {
    const h = historyRef.current;
    const idx = historyIdxRef.current;
    // Trim forward history
    historyRef.current = h.slice(0, idx + 1);
    historyRef.current.push(imageData);
    if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
    historyIdxRef.current = historyRef.current.length - 1;
    forceUpdate(n => n + 1);
  };

  const undo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current--;
    const ctx = canvasRef.current.getContext('2d');
    ctx.putImageData(historyRef.current[historyIdxRef.current], 0, 0);
    forceUpdate(n => n + 1);
  }, []);

  const redo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current++;
    const ctx = canvasRef.current.getContext('2d');
    ctx.putImageData(historyRef.current[historyIdxRef.current], 0, 0);
    forceUpdate(n => n + 1);
  }, []);

  // ─── Get pointer position relative to canvas ────────────
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  // ─── Drawing (pen / eraser) ──────────────────────────────
  const startDraw = useCallback((e) => {
    if (tool === 'text') return;
    e.preventDefault();
    drawingRef.current = true;
    const pos = getPos(e);
    lastPointRef.current = pos;
    pointsRef.current = [pos];

    const ctx = canvasRef.current.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = tool === 'eraser' ? brushSize * 3 : brushSize;

    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
    }

    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }, [tool, color, brushSize]);

  const draw = useCallback((e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const pts = pointsRef.current;
    pts.push(pos);

    const ctx = canvasRef.current.getContext('2d');

    // Bezier smoothing: use midpoints for quadratic curves
    if (pts.length >= 3) {
      const p0 = pts[pts.length - 3];
      const p1 = pts[pts.length - 2];
      const p2 = pos;
      const mid1 = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
      const mid2 = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      ctx.beginPath();
      ctx.moveTo(mid1.x, mid1.y);
      ctx.quadraticCurveTo(p1.x, p1.y, mid2.x, mid2.y);
      ctx.stroke();
    } else {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }

    lastPointRef.current = pos;
  }, []);

  const endDraw = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const ctx = canvasRef.current.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    // Save state
    pushHistory(ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height));
  }, []);

  // ─── Text tool ───────────────────────────────────────────
  const handleCanvasClick = useCallback((e) => {
    if (tool !== 'text') return;
    const pos = getPos(e);
    setTextInput(pos);
    setTextValue('');
  }, [tool]);

  const commitText = useCallback(() => {
    if (!textInput || !textValue.trim()) {
      setTextInput(null);
      setTextValue('');
      return;
    }
    const ctx = canvasRef.current.getContext('2d');
    const fontSize = Math.max(16, brushSize * 4);
    ctx.font = `${fontSize}px ${FONT}`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'top';
    ctx.fillText(textValue, textInput.x, textInput.y);
    pushHistory(ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height));
    setTextInput(null);
    setTextValue('');
  }, [textInput, textValue, color, brushSize]);

  // ─── Save: composite image + canvas → blob ──────────────
  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const img = imgRef.current;
      const canvas = canvasRef.current;
      const out = document.createElement('canvas');
      out.width = img.naturalWidth;
      out.height = img.naturalHeight;
      const ctx = out.getContext('2d');
      ctx.drawImage(img, 0, 0);
      // Scale edits canvas to original image size
      ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, img.naturalWidth, img.naturalHeight);
      out.toBlob((blob) => {
        if (blob) onSave(blob);
        setSaving(false);
      }, 'image/jpeg', 0.9);
    } catch (err) {
      console.error('[ImageEditor] save failed:', err);
      setSaving(false);
    }
  }, [saving, onSave]);

  // ─── Keyboard shortcuts ──────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, undo, redo]);

  const canUndo = historyIdxRef.current > 0;
  const canRedo = historyIdxRef.current < historyRef.current.length - 1;

  return (
    <div style={S.overlay}>
      {/* Top bar */}
      <div style={S.topBar}>
        <button onClick={onCancel} style={S.topBtn}>
          <X size={20} color={colors.white} />
          <span style={S.topBtnText}>Отмена</span>
        </button>
        <div style={S.topCenter}>
          <button onClick={undo} style={{ ...S.iconBtn, opacity: canUndo ? 1 : 0.3 }} disabled={!canUndo}>
            <Undo2 size={20} color={colors.white} />
          </button>
          <button onClick={redo} style={{ ...S.iconBtn, opacity: canRedo ? 1 : 0.3 }} disabled={!canRedo}>
            <Redo2 size={20} color={colors.white} />
          </button>
        </div>
        <button onClick={handleSave} style={{ ...S.topBtn, background: colors.avatarBlue, opacity: saving ? 0.6 : 1 }} disabled={saving}>
          <Send size={18} color={colors.white} />
          <span style={S.topBtnText}>{saving ? '...' : 'Отправить'}</span>
        </button>
      </div>

      {/* Canvas area */}
      <div ref={containerRef} style={S.canvasArea}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            ref={imgRef}
            src={imageUrl}
            alt="edit"
            onLoad={() => setImgLoaded(true)}
            draggable={false}
            style={{ display: 'block', userSelect: 'none', WebkitUserSelect: 'none' }}
          />
          <canvas
            ref={canvasRef}
            style={{ position: 'absolute', top: 0, left: 0, touchAction: 'none', cursor: tool === 'text' ? 'text' : 'crosshair' }}
            onPointerDown={startDraw}
            onPointerMove={draw}
            onPointerUp={endDraw}
            onPointerLeave={endDraw}
            onClick={handleCanvasClick}
          />
          {/* Text input overlay */}
          {textInput && (
            <div style={{
              position: 'absolute',
              top: (textInput.y / (canvasRef.current?.height || 1)) * 100 + '%',
              left: (textInput.x / (canvasRef.current?.width || 1)) * 100 + '%',
              transform: 'translate(0, -4px)',
            }}>
              <input
                autoFocus
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commitText(); if (e.key === 'Escape') setTextInput(null); }}
                onBlur={commitText}
                style={{
                  background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 6, padding: '4px 8px',
                  color: color, fontSize: Math.max(14, brushSize * 3),
                  fontFamily: FONT, outline: 'none', minWidth: 80,
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bottom toolbar */}
      <div style={S.bottomBar}>
        {/* Tools */}
        <div style={S.toolRow}>
          {[{ id: 'pen', Icon: Pen, label: 'Кисть' }, { id: 'text', Icon: Type, label: 'Текст' }, { id: 'eraser', Icon: Eraser, label: 'Ластик' }].map(({ id, Icon, label }) => (
            <button key={id} onClick={() => setTool(id)} style={{
              ...S.toolBtn,
              background: tool === id ? 'rgba(255,255,255,0.25)' : 'transparent',
            }}>
              <Icon size={20} color={colors.white} />
              <span style={S.toolLabel}>{label}</span>
            </button>
          ))}
        </div>

        {/* Color picker */}
        <div style={S.colorRow}>
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              style={{
                width: 28, height: 28, borderRadius: '50%', border: color === c ? '3px solid #fff' : '2px solid rgba(255,255,255,0.3)',
                background: c, cursor: 'pointer', flexShrink: 0, padding: 0,
                WebkitTapHighlightColor: 'transparent',
              }}
            />
          ))}
        </div>

        {/* Brush size */}
        <div style={S.sizeRow}>
          <span style={{ fontSize: 12, color: colors.gray400, fontFamily: FONT }}>Размер</span>
          <input
            type="range" min="1" max="20" value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
            style={{ flex: 1, accentColor: colors.avatarBlue }}
          />
          <div style={{
            width: Math.max(4, brushSize), height: Math.max(4, brushSize),
            borderRadius: '50%', background: tool === 'eraser' ? colors.gray400 : color,
            flexShrink: 0,
          }} />
        </div>
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────
const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 310,
    background: '#000',
    display: 'flex', flexDirection: 'column',
    fontFamily: FONT,
  },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 12px',
    paddingTop: 'max(8px, env(safe-area-inset-top))',
    background: 'rgba(17,24,39,0.95)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
  },
  topBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: radii.full,
    background: 'rgba(255,255,255,0.15)', border: 'none',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  topBtnText: {
    fontSize: 14, color: colors.white, fontFamily: FONT, fontWeight: 500,
  },
  topCenter: {
    display: 'flex', gap: 4,
  },
  iconBtn: {
    width: 40, height: 40, borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
    border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  canvasArea: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', background: '#000',
  },
  bottomBar: {
    padding: '8px 12px',
    paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
    background: 'rgba(17,24,39,0.95)',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  toolRow: {
    display: 'flex', gap: 4, justifyContent: 'center',
  },
  toolBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    padding: '6px 16px', borderRadius: radii.md,
    border: 'none', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  toolLabel: {
    fontSize: 11, color: colors.gray400, fontFamily: FONT,
  },
  colorRow: {
    display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap',
  },
  sizeRow: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
};
